/**
 * Customer sign-in prompt.
 *
 * State detection is intentionally isolated from placement and from the
 * card UI, so any piece can be replaced independently later (e.g. swapping
 * the cookie-based "known customer" check for a server-side/customer
 * account signal without touching markup or CSS).
 */
(function () {
  'use strict';

  var COOKIE_NAMES = {
    visitor: 'customer_prompt_visitor',
    knownCustomer: 'customer_prompt_known_customer',
    dismissedUntil: 'customer_prompt_dismissed_until',
  };

  /**
   * Small first-party cookie helper this component owns exclusively.
   * Never reads Shopify's own session cookies.
   */
  var Cookie = {
    read: function (name) {
      var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    },

    write: function (name, value, days) {
      var expires = '';
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + expires + '; path=/; SameSite=Lax';
    },

    remove: function (name) {
      document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    },
  };

  /**
   * Reads/writes the first-party visitor state this component owns.
   */
  var VisitorState = {
    read: function () {
      return {
        visitor: Cookie.read(COOKIE_NAMES.visitor) === 'true',
        knownCustomer: Cookie.read(COOKIE_NAMES.knownCustomer) === 'true',
        dismissedUntil: parseInt(Cookie.read(COOKIE_NAMES.dismissedUntil), 10) || 0,
      };
    },

    isDismissed: function (state) {
      return Date.now() < state.dismissedUntil;
    },

    markVisitor: function () {
      Cookie.write(COOKIE_NAMES.visitor, 'true', 365);
    },

    markKnownCustomer: function (value) {
      Cookie.write(COOKIE_NAMES.knownCustomer, value ? 'true' : 'false', 365);
    },

    dismiss: function (days) {
      var until = Date.now() + Math.max(days, 0) * 24 * 60 * 60 * 1000;
      Cookie.write(COOKIE_NAMES.dismissedUntil, String(until), days);
    },

    reset: function () {
      Cookie.remove(COOKIE_NAMES.visitor);
      Cookie.remove(COOKIE_NAMES.knownCustomer);
      Cookie.remove(COOKIE_NAMES.dismissedUntil);
    },
  };

  /**
   * Applies a dev-only query param override on top of the real state, for QA.
   * Only active when the section's "Enable test mode" setting is on.
   * ?customer_prompt_state=new|known|logged_in|reset
   */
  function applyDevOverride(state, customerLoggedIn) {
    var params = new URLSearchParams(window.location.search);
    var override = params.get('customer_prompt_state');

    if (!override) {
      return { state: state, customerLoggedIn: customerLoggedIn };
    }

    if (override === 'reset') {
      VisitorState.reset();
      return { state: VisitorState.read(), customerLoggedIn: customerLoggedIn };
    }

    if (override === 'new') {
      return {
        state: { visitor: false, knownCustomer: false, dismissedUntil: 0 },
        customerLoggedIn: false,
      };
    }

    if (override === 'known') {
      return {
        state: { visitor: true, knownCustomer: true, dismissedUntil: 0 },
        customerLoggedIn: false,
      };
    }

    if (override === 'logged_in') {
      return { state: state, customerLoggedIn: true };
    }

    return { state: state, customerLoggedIn: customerLoggedIn };
  }

  /** Finds the currently visible account-nav icon (mobile or desktop header). */
  function findVisibleAnchor() {
    var anchors = document.querySelectorAll('[data-customer-signin-prompt-anchor]');
    for (var i = 0; i < anchors.length; i++) {
      if (anchors[i].getClientRects().length > 0) return anchors[i];
    }
    return anchors[0] || null;
  }

  class CustomerSigninPrompt extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.customer-signin-prompt');
      if (!this.card) return;

      this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
      this.placementSetting = this.dataset.placementSetting || 'automatic';
      this.dismissalDays = parseFloat(this.dataset.dismissalDays) || 7;
      this.showDelaySeconds = Math.max(parseFloat(this.dataset.showDelay), 0) || 0;
      this.devControlsEnabled = this.dataset.devControls === 'true';
      this.handleReposition = this.repositionUnderAnchor.bind(this);

      this.bindEvents();
      this.evaluate();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleReposition);
      window.removeEventListener('orientationchange', this.handleReposition);
      window.removeEventListener('scroll', this.handleReposition);
      clearTimeout(this.showTimer);
    }

    bindEvents() {
      var closeButton = this.querySelector('[data-customer-signin-prompt-close]');
      var dismissButton = this.querySelector('[data-customer-signin-prompt-dismiss]');

      if (closeButton) {
        closeButton.addEventListener('click', this.handleDismiss.bind(this));
      }

      if (dismissButton) {
        dismissButton.addEventListener('click', this.handleDismiss.bind(this));
      }

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.handleDismiss();
      });
    }

    evaluate() {
      var state = VisitorState.read();
      var customerLoggedIn = this.customerLoggedIn;

      if (this.devControlsEnabled) {
        var overridden = applyDevOverride(state, customerLoggedIn);
        state = overridden.state;
        customerLoggedIn = overridden.customerLoggedIn;
      }

      // Logged-in customers never see the prompt, but this browser is now
      // known to belong to a real customer for future logged-out visits.
      if (customerLoggedIn) {
        VisitorState.markKnownCustomer(true);
        this.hide();
        return;
      }

      if (VisitorState.isDismissed(state)) {
        this.hide();
        return;
      }

      if (!state.visitor) {
        VisitorState.markVisitor();
      }

      var placement = this.resolvePlacement(state);
      this.setAttribute('data-placement', placement);

      clearTimeout(this.showTimer);
      this.showTimer = setTimeout(
        this.reveal.bind(this, placement),
        this.showDelaySeconds * 1000
      );
    }

    reveal(placement) {
      this.show();

      if (placement === 'account') {
        // Measure after paint so width/height are accurate when positioning.
        requestAnimationFrame(() => {
          this.repositionUnderAnchor();
        });
        window.addEventListener('resize', this.handleReposition);
        window.addEventListener('orientationchange', this.handleReposition);
        window.addEventListener('scroll', this.handleReposition, { passive: true });
      } else {
        window.removeEventListener('resize', this.handleReposition);
        window.removeEventListener('orientationchange', this.handleReposition);
        window.removeEventListener('scroll', this.handleReposition);
        this.style.top = '';
        this.style.left = '';
        this.style.right = '';
      }
    }

    resolvePlacement(state) {
      if (this.placementSetting === 'bottom') return 'bottom';
      if (this.placementSetting === 'account') return 'account';
      return state.knownCustomer ? 'account' : 'bottom';
    }

    /** Positions the popup below the account nav icon, clamped to the viewport. */
    repositionUnderAnchor() {
      var anchor = findVisibleAnchor();
      if (!anchor) return;

      var anchorRect = anchor.getBoundingClientRect();
      var gutter = 12;
      var width = this.offsetWidth || 220;
      // Prefer aligning the popup's right edge with the icon (account is on the right).
      var left = anchorRect.right - width;
      var maxLeft = window.innerWidth - width - gutter;
      left = Math.min(Math.max(left, gutter), Math.max(maxLeft, gutter));

      this.style.top = Math.round(anchorRect.bottom + 8) + 'px';
      this.style.left = Math.round(left) + 'px';
      this.style.right = 'auto';
    }

    handleDismiss() {
      VisitorState.dismiss(this.dismissalDays);
      this.hide();
    }

    show() {
      this.removeAttribute('hidden');
    }

    hide() {
      clearTimeout(this.showTimer);
      this.setAttribute('hidden', '');
      window.removeEventListener('resize', this.handleReposition);
      window.removeEventListener('orientationchange', this.handleReposition);
      window.removeEventListener('scroll', this.handleReposition);
    }
  }

  if (!window.customElements.get('customer-signin-prompt')) {
    window.customElements.define('customer-signin-prompt', CustomerSigninPrompt);
  }
})();
